/**
 * Color palette for the application
 */
const colors = {
  white: 'hsl(0, 0%, 100%)',
  black: 'hsl(0, 0%, 0%)',
  gray: {
    '100': 'hsl(210, 8%, 95%)',
    '200': 'hsl(210, 8%, 90%)',
    '300': 'hsl(210, 9%, 75%)',
    '400': 'hsl(210, 8%, 42%)',
    '500': 'hsl(210, 8%, 25%)',
    '600': 'hsl(210, 8%, 5%)',
  },
  orange: {
    '100': 'hsl(25, 85%, 92%)',
    '200': 'hsl(25, 85%, 82%)',
    '300': 'hsl(25, 85%, 68%)',
    '400': 'hsl(25, 85%, 50%)',
    '500': 'hsl(25, 85%, 45%)',
    '600': 'hsl(25, 85%, 35%)',
  },
  blue: {
    '100': 'hsl(211, 100%, 95%)',
    '200': 'hsl(211, 100%, 85%)',
    '300': 'hsl(211, 100%, 70%)',
    '400': 'hsl(211, 100%, 50%)',
    '500': 'hsl(211, 100%, 40%)',
    '600': 'hsl(211, 100%, 30%)',
  },
  green: {
    '100': 'hsl(134, 65%, 92%)',
    '200': 'hsl(134, 65%, 82%)',
    '300': 'hsl(134, 65%, 65%)',
    '400': 'hsl(134, 65%, 39%)',
    '500': 'hsl(134, 65%, 29%)',
    '600': 'hsl(134, 65%, 19%)',
  },
  red: {
    '100': 'hsl(0, 100%, 95%)',
    '200': 'hsl(0, 100%, 85%)',
    '300': 'hsl(0, 100%, 70%)',
    '400': 'hsl(0, 100%, 50%)',
    '500': 'hsl(0, 100%, 40%)',
    '600': 'hsl(0, 100%, 30%)',
  },
  yellow: {
    '100': 'hsl(43, 85%, 95%)',
    '200': 'hsl(43, 85%, 88%)',
    '300': 'hsl(43, 85%, 72%)',
    '400': 'hsl(43, 85%, 50%)',
    '500': 'hsl(43, 85%, 33%)',
    '600': 'hsl(43, 85%, 18%)',
  },
  purple: {
    '100': 'hsl(277, 88%, 92%)',
    '200': 'hsl(277, 88%, 82%)',
    '300': 'hsl(277, 88%, 68%)',
    '400': 'hsl(277, 88%, 56%)',
    '500': 'hsl(277, 88%, 45%)',
    '600': 'hsl(277, 88%, 35%)',
  },
  gold: {
    '100': 'hsl(46, 100%, 91%)',
    '200': 'hsl(46, 100%, 74%)',
    '300': 'hsl(45, 100%, 42%)',
    '400': 'hsl(46, 92%, 26%)',
  },
  silver: {
    '100': 'hsl(0, 0%, 95%)',
    '200': 'hsl(0, 0%, 84%)',
    '300': 'hsl(210, 5%, 68%)',
    '400': 'hsl(210, 2%, 40%)',
  },
  bronze: {
    '100': 'hsl(28, 40%, 92%)',
    '200': 'hsl(30, 47%, 83%)',
    '300': 'hsl(28, 43%, 65%)',
    '400': 'hsl(28, 43%, 39%)',
  },
};

/**
 * Theme for the application
 */
const theme = {
  primary: {
    lighter: colors.blue['300'],
    light: colors.blue['400'],
    dark: colors.blue['500'],
    darker: colors.blue['600'],
  },
  secondary: {
    light: '#030456',
    dark: '#ffffff',
  },
  background: {
    lighter: colors.gray['100'],
    light: colors.gray['200'],
    dark: colors.gray['400'],
    darker: colors.gray['500'],
    paper: colors.white,
  },

  text: {
    primary: {
      light: colors.black,
      dark: colors.white,
    },
    secondary: {
      light: colors.blue['500'],
      dark: colors.blue['300'],
    },
  },
  fontFamily:
    '-apple-system, system-ui, "Segoe UI Adjusted", "Segoe UI", "Liberation Sans", sans-serif',
  fontSize: {
    default: '16px',
    h1: '32px',
    h2: '28px',
    h3: '24px',
    h4: '22px',
    h5: '20px',
    h6: '18px',
    smaller: '14px',
    smallest: '12px',
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    bold: 700,
  },
  padding: {
    xs: '5px',
    s: '10px',
    m: '20px',
    l: '40px',
    xl: '80px',
  },
  gap: {
    xs: '5px',
    s: '10px',
    m: '20px',
    l: '40px',
    xl: '80px',
  },
  lineHeight: {
    default: '1.0',
    xs: '0.5',
    s: '1.5',
    m: '2',
    l: '3',
  },
  margin: {
    xs: '5px',
    s: '10px',
    m: '20px',
    l: '40px',
    xl: '80px',
  },
  borderRadius: {
    xs: '4px',
    s: '8px',
    m: '16px',
    l: '32px',
  },
  borderColor: colors.blue['600'],
  boxShadowBorder: '5px',
  boxShadowColor: 'rgb(193, 211, 252)',
  buttonShadow: (color: string) => `0 0 ${theme.boxShadowBorder} ${color}`,
  border: `1px solid ${colors.gray['300']}`,
  containerShadow: `2px 2px 5px 5px rgb(193, 211, 252)`,
  iconSize: 80,
};

export { theme, colors };
